// Single source of truth for the Flask API base URL and URL-building helper.
// Previously re-declared ~20 times across src/ with three drifting fallback
// chains (12 files -> https://api.bagholders.ai, 7 pages -> localhost:5000,
// Dashboard.js had its own hostname-sniffing variant). If VITE_API_URL
// is unset/mistyped at build time, only this file needs fixing.
//
// Fallback chain: VITE_API_URL env var -> loopback default when the
// page itself is served from a loopback hostname -> production default.
//
// `import.meta.env` is Vite's build-time env object (it replaced CRA's
// process.env.REACT_APP_* when the toolchain moved off react-scripts). The
// guards keep this file importable from a non-Vite context (a plain Node
// require, a bundler that leaves import.meta undefined) -- an unset var
// falls through to exactly the same loopback/production defaults as before.
const LOOPBACK_HOSTNAMES = ['127.0.0.1', 'localhost', '[::1]'];
const LOOPBACK_API_BASE_URL = 'http://127.0.0.1:5000';
const PROD_API_BASE_URL = 'https://api.bagholders.ai';

// When the Flask API is serving this page itself, the API is the page's own
// origin. That happens on the tailnet bind: the owner opens
// http://bookmaker:5000 (or the raw 100.x address, or the MagicDNS
// *.ts.net name) and Flask hands back the built SPA.
//
// The base MUST be same-origin there, and this rule has to beat VITE_API_URL:
// a build baked with https://api.bagholders.ai would send the owner's calls
// back out through the public Cloudflare tunnel, which lands on the API over
// LOOPBACK -- and loopback is a visitor, so every owner-only route would 401
// on a page the owner reached over the tailnet. Same-origin keeps the peer
// address a tailnet address all the way through.
// The same is true of the vanity hostname: book.bagholders.ai is a public DNS
// A record pointing at the box's TAILSCALE address, fronted by Caddy
// (Trader/ops/trader-book.caddy), which proxies /api to the API's tailnet bind.
// Same-origin there for the same reason.
export const OWNER_HOSTS = ['book.bagholders.ai'];
const MAGIC_DNS_NAME = 'bookmaker';        // this box's Tailscale machine name
const SAME_ORIGIN_API_BASE_URL = '';

function isTailnetIPv4(hostname) {
  const octets = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
  if (!octets) return false;
  const [a, b, c, d] = octets.slice(1).map(Number);
  if ([a, b, c, d].some((n) => n > 255)) return false;
  // 100.64.0.0/10 -- Tailscale's CGNAT block.
  return a === 100 && b >= 64 && b <= 127;
}

// Exported for the test: this predicate decides whether the page is being
// served BY the API, which is the whole of the same-origin rule.
export function isApiOriginHost(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  if (OWNER_HOSTS.includes(host)) return true;
  if (host === MAGIC_DNS_NAME || host.startsWith(`${MAGIC_DNS_NAME}.`)) return true;
  if (host.endsWith('.ts.net')) return true;
  return isTailnetIPv4(host);
}

export function resolveApiBaseUrl(hostname, envApiUrl) {
  if (isApiOriginHost(hostname)) return SAME_ORIGIN_API_BASE_URL;
  if (envApiUrl) return envApiUrl;
  if (LOOPBACK_HOSTNAMES.includes(hostname)) return LOOPBACK_API_BASE_URL;
  return PROD_API_BASE_URL;
}

const ENV_API_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) || '';

export const API_BASE_URL = resolveApiBaseUrl(
  typeof window !== 'undefined' ? window.location.hostname : '',
  ENV_API_URL
);

const API_PATH_PREFIX = '/api';

// Builds a full API URL. Pass { bust: true } to append a 30-second
// cache-bust bucket so CDN/browser caches don't serve stale data after a
// backend pull (buckets dedupe burst requests within the same window while
// still bypassing typical CDN cache lifetimes).
export function buildApiUrl(endpoint, { bust = false } = {}) {
  if (!bust) return `${API_BASE_URL}${API_PATH_PREFIX}${endpoint}`;
  const sep = endpoint.includes('?') ? '&' : '?';
  const bucket = Math.floor(Date.now() / 30000);
  return `${API_BASE_URL}${API_PATH_PREFIX}${endpoint}${sep}_t=${bucket}`;
}
